import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/data/company";
import { submitEnquiry } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone number.");
      return;
    }
    try {
      setLoading(true);
      await submitEnquiry({ ...form, source: "contact-page" });
      toast.success("Thanks! Our team will reach out within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        message: "",
      });
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us — Vortex Power Solutions</title>
        <meta
          name="description"
          content="Reach Vortex Power Solutions — Madipakkam, Chennai. Call, WhatsApp or send an enquiry for quotes and support."
        />
        <link rel="canonical" href="https://www.vortexworld.in/contact" />
      </Helmet>

      {/* Hero — lighter tint */}
      <section className="bg-vortex-tint text-vortex-navy py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-light opacity-70 pointer-events-none" />
        <div className="container-wide relative">
          <div className="max-w-3xl">
            <div className="section-label mb-5">
              Contact
            </div>
            <h1 className="font-heading font-extrabold text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] mb-5">
              Let&apos;s talk power.
            </h1>
            <p className="text-lg text-vortex-slate max-w-2xl">
              Whether it&apos;s a load analysis, a custom build, or after-sales
              support — our engineers are ready to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="bg-white py-16">
        <div className="container-wide grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              Icon: Phone,
              label: "Customer Care",
              value: COMPANY.phone,
              href: `tel:${COMPANY.phone}`,
            },
            {
              Icon: Mail,
              label: "Product Enquiry",
              value: COMPANY.email,
              href: `mailto:${COMPANY.email}`,
            },
            {
              Icon: Clock,
              label: "Business Hours",
              value: COMPANY.hours,
            },
          ].map((c, i) => (
            <a
              key={i}
              href={c.href || "#"}
              className="block bg-vortex-alt border border-vortex-border rounded-2xl p-6 hover:border-vortex-blue/40 hover:bg-white hover:shadow-lg transition-all"
              data-testid={`contact-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-white border border-vortex-border flex items-center justify-center mb-4">
                <c.Icon className="w-5 h-5 text-vortex-blue" strokeWidth={1.5} />
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-vortex-slate mb-1">
                {c.label}
              </div>
              <div className="font-heading font-bold text-vortex-navy">
                {c.value}
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Map + Form */}
      <section className="bg-vortex-alt py-20">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="section-label mb-4">Reach Us</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-6">
              Head office & factory.
            </h2>
            <div className="bg-white border border-vortex-border rounded-2xl p-6 mb-4">
              <div className="flex items-start gap-3">
                <MapPin
                  className="w-5 h-5 text-vortex-blue mt-0.5 flex-shrink-0"
                  strokeWidth={2}
                />
                <div className="text-vortex-navy leading-relaxed">
                  <div className="font-semibold">{COMPANY.name}</div>
                  <div className="text-vortex-slate mt-1 text-sm">
                    {COMPANY.address.line1},<br />
                    {COMPANY.address.line2},<br />
                    {COMPANY.address.city}, {COMPANY.address.state} –{" "}
                    {COMPANY.address.zip},<br />
                    {COMPANY.address.country}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-vortex-border h-72 md:h-80">
              <iframe
                title="Vortex Location"
                src="https://www.google.com/maps?q=Madipakkam,%20Chennai&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              ></iframe>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl border border-vortex-border p-7 md:p-10 shadow-sm">
              <div className="section-label mb-3">Send a Message</div>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-6">
                Tell us how we can help.
              </h2>

              <form
                onSubmit={onSubmit}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                data-testid="contact-form"
              >
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-vortex-navy">
                    Name *
                  </label>
                  <Input
                    required
                    value={form.name}
                    onChange={update("name")}
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border h-11 focus:border-vortex-blue"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-vortex-navy">
                    Phone *
                  </label>
                  <Input
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border h-11 focus:border-vortex-blue"
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-vortex-navy">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border h-11 focus:border-vortex-blue"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-vortex-navy">
                    Company
                  </label>
                  <Input
                    value={form.company}
                    onChange={update("company")}
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border h-11 focus:border-vortex-blue"
                    data-testid="contact-company"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-vortex-navy">
                    Subject
                  </label>
                  <Input
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="Servo Stabilizer 100 kVA quote"
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border h-11 focus:border-vortex-blue"
                    data-testid="contact-subject"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-widest text-vortex-navy">
                    Message
                  </label>
                  <Textarea
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Share your requirement, load rating, timeline…"
                    className="mt-1.5 rounded-xl bg-vortex-alt border-vortex-border focus:border-vortex-blue"
                    data-testid="contact-message"
                  />
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-xs text-vortex-slate">
                    We respond within 24 hours (Mon–Sat).
                  </p>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white h-12 px-8 font-semibold"
                    data-testid="contact-submit"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {loading ? "Sending…" : "Send Message"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
